<template>
  <div class="starter-tabs__inner">
    <div
      class="starter-tabs__tablist"
      role="tablist"
      aria-label="Product information tabs"
    >
      <button
        v-for="(tab, index) in tabs"
        :id="`starter-tab-${tab.id}`"
        :key="tab.id"
        class="starter-tabs__tab h6"
        :class="{ 'starter-tabs__tab--active': activeTab === tab.id }"
        role="tab"
        type="button"
        :aria-selected="activeTab === tab.id ? 'true' : 'false'"
        :aria-controls="`starter-panel-${tab.id}`"
        :tabindex="activeTab === tab.id ? 0 : -1"
        @click="selectTab(tab.id)"
        @keydown="onTabKeydown($event, index)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div
      ref="panels"
      class="starter-tabs__panels"
    >
      <slot />
    </div>

    <div
      v-if="showNutritionModal && nutritionImageUrl"
      class="popup-wrapper starter-tabs__nutrition-modal"
      data-nutrition-modal
      role="dialog"
      aria-modal="true"
      aria-label="Nutrition table"
      @click="onNutritionBackdropClick"
    >
      <div
        class="popup starter-tabs__nutrition-popup"
        @click.stop
      >
        <button
          type="button"
          class="popup__close close-btn xs font-semibold flex items-center"
          data-nutrition-close
          @click="closeNutritionModal"
        >
          Close
          <CloseIcon />
        </button>

        <img
          :src="nutritionImageUrl"
          :srcset="nutritionImageSrcset || null"
          :width="nutritionImageWidth || null"
          :height="nutritionImageHeight || null"
          sizes="(min-width: 760px) 720px, 90vw"
          alt=""
          class="starter-tabs__nutrition-image"
          decoding="async"
          loading="lazy"
        >
      </div>
    </div>
  </div>
</template>

<script>
import CloseIcon from '../../icons/close-icon'

const TAB_ORDER = ['content', 'faqs', 'reviews']

export default {
  name: 'StarterTabs',

  components: { CloseIcon },

  props: {
    defaultTab: {
      type: String,
      default: 'content'
    },
    tabContentLabel: {
      type: String,
      default: 'What to Expect'
    },
    tabFaqsLabel: {
      type: String,
      default: 'FAQs'
    },
    tabReviewsLabel: {
      type: String,
      default: 'Reviews'
    },
    nutritionImageSrcset: {
      type: String,
      default: ''
    },
    nutritionImageWidth: {
      type: [String, Number],
      default: ''
    },
    nutritionImageHeight: {
      type: [String, Number],
      default: ''
    },
    nutritionImageUrl: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      activeTab: this.resolveDefaultTab(),
      showNutritionModal: false
    }
  },

  computed: {
    tabs () {
      return [
        { id: 'content', label: this.tabContentLabel },
        { id: 'faqs', label: this.tabFaqsLabel },
        { id: 'reviews', label: this.tabReviewsLabel }
      ]
    }
  },

  watch: {
    activeTab () {
      this.syncPanels()
    }
  },

  mounted () {
    this.syncPanels()
    this.$el.addEventListener('click', this.onRootClick)
    document.addEventListener('keydown', this.onDocumentKeydown)
  },

  beforeUnmount () {
    this.$el.removeEventListener('click', this.onRootClick)
    document.removeEventListener('keydown', this.onDocumentKeydown)
  },

  methods: {
    resolveDefaultTab () {
      return TAB_ORDER.includes(this.defaultTab) ? this.defaultTab : 'content'
    },

    selectTab (tabId) {
      if (!TAB_ORDER.includes(tabId) || this.activeTab === tabId) {
        return
      }

      this.activeTab = tabId
    },

    syncPanels () {
      const panelsRoot = this.$refs.panels

      if (!panelsRoot) {
        return
      }

      panelsRoot.querySelectorAll('[data-tab-panel]').forEach((panel) => {
        const panelId = panel.getAttribute('data-tab-panel')
        const isActive = panelId === this.activeTab

        panel.classList.toggle('starter-tabs__panel--active', isActive)
        panel.hidden = !isActive
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true')
        panel.setAttribute('tabindex', isActive ? '0' : '-1')

        if (isActive) {
          panel.setAttribute('aria-labelledby', `starter-tab-${panelId}`)
        }
      })
    },

    onRootClick (event) {
      if (event.target.closest('[data-nutrition-trigger]')) {
        this.openNutritionModal()
      }
    },

    openNutritionModal () {
      if (!this.nutritionImageUrl) {
        return
      }

      this.showNutritionModal = true
    },

    closeNutritionModal () {
      this.showNutritionModal = false
    },

    onNutritionBackdropClick (event) {
      if (event.target.closest('[data-nutrition-close]')) {
        this.closeNutritionModal()
        return
      }

      if (event.target.hasAttribute('data-nutrition-modal')) {
        this.closeNutritionModal()
      }
    },

    onDocumentKeydown (event) {
      if (event.key === 'Escape' && this.showNutritionModal) {
        this.closeNutritionModal()
      }
    },

    onTabKeydown (event, index) {
      let nextIndex = index

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (index + 1) % this.tabs.length
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (index - 1 + this.tabs.length) % this.tabs.length
          break
        case 'Home':
          nextIndex = 0
          break
        case 'End':
          nextIndex = this.tabs.length - 1
          break
        default:
          return
      }

      event.preventDefault()
      const nextTab = this.tabs[nextIndex]
      const nextButton = this.$el.querySelector(`#starter-tab-${nextTab.id}`)

      if (nextButton) {
        nextButton.focus()
      }

      this.selectTab(nextTab.id)
    }
  }
}
</script>
